import { ResponsiveTable } from '../components/ResponsiveTable';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { apiClient } from '../utils/api';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { HelpIcon } from '../components/HelpIcon';
import { ConfirmModal } from '../components/ConfirmModal';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { RootState } from '../store';
import {
  Activity,
  ChevronUp,
  ChevronDown,
  Filter,
  ArrowLeft,
  LogIn,
  LogOut,
  UserCheck,
  Lock,
  Calendar,
  FileText,
  Settings,
  Trash2,
} from 'lucide-react';
import { DateInput } from '../components/DateInput';

interface ActivityLogItem {
  id: string;
  action: string;
  description: string;
  type: string;
  ipAddress: string;
  userAgent: string;
  timestamp: string;
  success: boolean;
  userId: string | null;
  userEmail: string | null;
  client: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null;
}

const TYPE_OPTIONS = ['all', 'security', 'profile', 'appointment', 'document', 'settings'] as const;

interface ClientActivityLogsPageProps {
  clientId?: string;
}

export function ClientActivityLogsPage({ clientId: clientIdProp }: ClientActivityLogsPageProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: clientIdFromRoute } = useParams<{ id: string }>();
  const clientId = clientIdProp || clientIdFromRoute;
  const { user } = useSelector((state: RootState) => state.auth);
  const canHardDelete = user?.role === 'super_admin';
  const { needsShopSelection, canLoad, currentShopId } = useShopScopedPage();

  const [items, setItems] = useState<ActivityLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clientName, setClientName] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [softDeleteModalOpen, setSoftDeleteModalOpen] = useState(false);
  const [hardDeleteModalOpen, setHardDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const [filters, setFilters] = useState({
    action: '',
    type: '',
    success: '',
    search: '',
    dateFrom: '',
    dateTo: '',
    limit: '50',
    offset: '0',
    sortBy: 'timestamp',
    sortOrder: 'desc',
  });

  useEffect(() => {
    if (!canLoad) return;
    loadActivities();
  }, [filters, clientId, canLoad, currentShopId]);

  const loadActivities = async () => {
    if (!canLoad) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      if (clientId) params.set('clientId', clientId);

      const endpoint = clientId
        ? `/api/admin/clients/${clientId}/activity`
        : '/api/admin/activity-logs';

      const response = await apiClient(`${endpoint}?${params}`);

      if (!response.ok) {
        throw new Error('Failed to load activity logs');
      }

      const data = await response.json();
      if (data.success) {
        const loaded = data.data.items || [];
        setItems(loaded);
        setTotal(data.data.total || 0);
        if (clientId && loaded[0]?.client) {
          setClientName(`${loaded[0].client.firstName} ${loaded[0].client.lastName}`);
        } else if (clientId) {
          const clientRes = await apiClient(`/api/admin/clients/${clientId}`);
          if (clientRes.ok) {
            const clientData = await clientRes.json();
            const c = clientData.data?.client || clientData.client;
            if (c) setClientName(`${c.firstName} ${c.lastName}`);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load activity logs:', error);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateTime: string) =>
    new Date(dateTime).toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

  const handleSort = (field: string) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'desc' ? 'asc' : 'desc',
    }));
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (filters.sortBy !== field) return null;
    return filters.sortOrder === 'asc' ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  const getActivityIcon = (type: string, action: string) => {
    if (action === 'LOGIN') return <LogIn className="w-4 h-4" />;
    if (action === 'LOGOUT') return <LogOut className="w-4 h-4" />;
    if (action === 'EMAIL_VERIFIED') return <UserCheck className="w-4 h-4" />;
    if (action.includes('PASSWORD') || action.includes('LOGIN')) return <Lock className="w-4 h-4" />;
    if (type === 'appointment') return <Calendar className="w-4 h-4" />;
    if (type === 'document') return <FileText className="w-4 h-4" />;
    if (type === 'settings') return <Settings className="w-4 h-4" />;
    return <Activity className="w-4 h-4" />;
  };

  const offset = parseInt(filters.offset, 10) || 0;
  const limit = parseInt(filters.limit, 10) || 50;

  const visibleIds = useMemo(() => items.map((item) => item.id), [items]);
  const {
    selectedArray,
    selectedCount,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    someVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleIds);

  const showToast = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 6000);
  };

  const confirmBulkSoftDelete = async () => {
    if (selectedCount === 0) return;
    try {
      setDeleteLoading(true);
      const res = await apiClient(
        '/api/admin/activity-logs/bulk-soft-delete',
        {
          method: 'POST',
          body: JSON.stringify({ ids: selectedArray }),
        },
        true,
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || t('admin.activityLogs.bulkDelete.error'));
      const failed = data.data?.failed as Array<{ id: string; message: string }> | undefined;
      const deleted = (data.data?.deleted as string[] | undefined)?.length ?? 0;
      if (failed?.length) {
        showToast(data.message || t('admin.activityLogs.bulkDelete.partialSoft'), 'warning');
      } else {
        showToast(t('admin.activityLogs.bulkDelete.softSuccess', { count: deleted }), 'success');
      }
      setSoftDeleteModalOpen(false);
      clearSelection();
      await loadActivities();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('admin.activityLogs.bulkDelete.error'), 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirmBulkHardDelete = async () => {
    if (selectedCount === 0) return;
    try {
      setDeleteLoading(true);
      const res = await apiClient(
        '/api/admin/activity-logs/bulk-hard-delete',
        {
          method: 'POST',
          body: JSON.stringify({ ids: selectedArray }),
        },
        true,
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || t('admin.activityLogs.bulkDelete.error'));
      const failed = data.data?.failed as Array<{ id: string; message: string }> | undefined;
      const deleted = (data.data?.deleted as string[] | undefined)?.length ?? 0;
      if (failed?.length) {
        showToast(data.message || t('admin.activityLogs.bulkDelete.partialHard'), 'warning');
      } else {
        showToast(t('admin.activityLogs.bulkDelete.hardSuccess', { count: deleted }), 'success');
      }
      setHardDeleteModalOpen(false);
      clearSelection();
      await loadActivities();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('admin.activityLogs.bulkDelete.error'), 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (needsShopSelection) {
    return <ShopSelectionGate />;
  }

  return (
    <div className="space-y-6">
      <div className="dt-list-toolbar flex justify-between items-start">
        <div>
          {clientId && (
            <button
              onClick={() => navigate('/clients')}
              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 mb-3"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('admin.activityLogs.backToClients')}
            </button>
          )}
          <h1 className="text-2xl font-bold text-textPrimary flex items-center gap-2">
            <Activity className="w-7 h-7" />
            {clientId
              ? t('admin.activityLogs.clientTitle', { name: clientName || '...' })
              : t('admin.activityLogs.title')}
            <HelpIcon
              docPath={clientId ? '29-journal-activites-clients#29-3-consultation-par-client' : '29-journal-activites-clients'}
              tooltip="Documentation : Journal d'activités clients"
            />
          </h1>
          <p className="text-textSecondary mt-1">
            {clientId ? t('admin.activityLogs.clientSubtitle') : t('admin.activityLogs.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="btn-secondary flex items-center gap-2"
        >
          <Filter className="w-4 h-4" />
          {t('admin.activityLogs.filters')}
        </button>
      </div>

      {showFilters && (
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="dt-filters grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">
                {t('admin.activityLogs.filter.search')}
              </label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, offset: '0' })}
                placeholder={t('admin.activityLogs.filter.searchPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">
                {t('admin.activityLogs.filter.action')}
              </label>
              <input
                type="text"
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value, offset: '0' })}
                placeholder="LOGIN, PROFILE_UPDATE..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">
                {t('admin.activityLogs.filter.type')}
              </label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value, offset: '0' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt === 'all' ? '' : opt}>
                    {t(`admin.activityLogs.types.${opt}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">
                {t('admin.activityLogs.filter.success')}
              </label>
              <select
                value={filters.success}
                onChange={(e) => setFilters({ ...filters, success: e.target.value, offset: '0' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">{t('admin.activityLogs.filter.all')}</option>
                <option value="true">{t('admin.activityLogs.filter.successYes')}</option>
                <option value="false">{t('admin.activityLogs.filter.successNo')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">
                {t('admin.activityLogs.filter.dateFrom')}
              </label>
              <DateInput
                value={filters.dateFrom}
                onChange={(value) => setFilters({ ...filters, dateFrom: value, offset: '0' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">
                {t('admin.activityLogs.filter.dateTo')}
              </label>
              <DateInput
                value={filters.dateTo}
                onChange={(value) => setFilters({ ...filters, dateTo: value, offset: '0' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap justify-between items-center gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-textSecondary">
              {t('admin.activityLogs.showing', { count: items.length, total, offset })}
            </p>
            {selectedCount > 0 && (
              <span className="text-sm text-primary-600 font-medium">
                {t('admin.activityLogs.bulkDelete.selected', { count: selectedCount })}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedCount > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setSoftDeleteModalOpen(true)}
                  disabled={deleteLoading}
                  className="btn-secondary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('admin.activityLogs.bulkDelete.soft', { count: selectedCount })}
                </button>
                {canHardDelete && (
                  <button
                    type="button"
                    onClick={() => setHardDeleteModalOpen(true)}
                    disabled={deleteLoading}
                    className="text-sm inline-flex items-center gap-1.5 px-3 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('admin.activityLogs.bulkDelete.hard', { count: selectedCount })}
                  </button>
                )}
              </>
            )}
            <button
              disabled={offset <= 0}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  offset: String(Math.max(0, offset - limit)),
                }))
              }
              className="btn-secondary text-sm disabled:opacity-40"
            >
              {t('common.previous')}
            </button>
            <button
              disabled={offset + limit >= total}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  offset: String(offset + limit),
                }))
              }
              className="btn-secondary text-sm disabled:opacity-40"
            >
              {t('common.next')}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-textSecondary">{t('admin.activityLogs.loading')}</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-textSecondary">{t('admin.activityLogs.empty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <ResponsiveTable className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      disabled={items.length === 0}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      aria-label={t('admin.activityLogs.bulkDelete.selectAll')}
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    <button onClick={() => handleSort('timestamp')} className="hover:text-gray-700">
                      {t('admin.activityLogs.columns.date')}
                      <SortIcon field="timestamp" />
                    </button>
                  </th>
                  {!clientId && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {t('admin.activityLogs.columns.client')}
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    <button onClick={() => handleSort('action')} className="hover:text-gray-700">
                      {t('admin.activityLogs.columns.action')}
                      <SortIcon field="action" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('admin.activityLogs.columns.description')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('admin.activityLogs.columns.type')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('admin.activityLogs.columns.ip')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    <button onClick={() => handleSort('success')} className="hover:text-gray-700">
                      {t('admin.activityLogs.columns.success')}
                      <SortIcon field="success" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item) => (
                  <tr key={item.id} className={`hover:bg-gray-50 ${selectedArray.includes(item.id) ? 'bg-primary-50/40' : ''}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedArray.includes(item.id)}
                        onChange={() => toggleSelect(item.id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        aria-label={t('admin.activityLogs.bulkDelete.selectRow')}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      {formatDateTime(item.timestamp)}
                    </td>
                    {!clientId && (
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {item.client ? (
                          <button
                            onClick={() => navigate(`/clients/${item.client!.id}/activity`)}
                            className="text-primary-600 hover:text-primary-700"
                          >
                            {item.client.firstName} {item.client.lastName}
                          </button>
                        ) : (
                          <span className="text-gray-400">{item.userEmail || '—'}</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        {getActivityIcon(item.type, item.action)}
                        <span className="font-mono text-xs">{item.action}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate" title={item.description}>
                      {item.description}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {t(`admin.activityLogs.types.${item.type}`, { defaultValue: item.type })}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono text-xs">
                      {item.ipAddress || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.success
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {item.success
                          ? t('admin.activityLogs.filter.successYes')
                          : t('admin.activityLogs.filter.successNo')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          </div>
        )}
      </div>

      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm text-white ${
            toast.type === 'success'
              ? 'bg-green-600'
              : toast.type === 'warning'
                ? 'bg-amber-600'
                : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}

      <ConfirmModal
        isOpen={softDeleteModalOpen}
        title={t('admin.activityLogs.bulkDelete.softTitle')}
        message={t('admin.activityLogs.bulkDelete.softMessage', { count: selectedCount })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        confirmVariant="warning"
        isLoading={deleteLoading}
        onConfirm={confirmBulkSoftDelete}
        onClose={() => !deleteLoading && setSoftDeleteModalOpen(false)}
      />

      <ConfirmDefinitiveDeleteModal
        isOpen={hardDeleteModalOpen}
        onConfirm={confirmBulkHardDelete}
        onClose={() => !deleteLoading && setHardDeleteModalOpen(false)}
        entityLabel={t('admin.activityLogs.bulkDelete.entityLabel')}
        count={selectedCount}
        isLoading={deleteLoading}
      />
    </div>
  );
}
